import type { ExcuseKind } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { errorMessage, get, post } from "../api";
import { Alert, Button, DateOfBirthField, Loading } from "../components/ui";
import { Frame } from "./Forms";

/** A client's private link to a school or work note: date of birth first, then open or download the PDF. */
export function NoteLinkPage() {
  const { token = "" } = useParams();
  const base = `/api/note-links/${encodeURIComponent(token)}`;
  const info = useQuery({ queryKey: ["note-link", token], queryFn: () => get<{ clinicName: string; kind: ExcuseKind }>(base), retry: false });
  const [dob, setDob] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const clinic = info.data?.clinicName ?? "";
  const what = info.data?.kind === "work" ? "work note" : "school note";

  if (info.error)
    return (
      <Frame clinic="" title="Note">
        <Alert>{errorMessage(info.error)}</Alert>
      </Frame>
    );
  if (!info.data)
    return (
      <Frame clinic="" title="Note">
        <Loading />
      </Frame>
    );
  if (open)
    return (
      <Frame clinic={clinic} title="Note">
        <div className="mx-auto flex max-w-sm flex-col gap-4">
          <h1 className="font-display text-2xl">Your {what}</h1>
          <p className="text-ink-soft">Open it to print, or save the PDF to send it on.</p>
          <a className="rounded-[var(--radius-control)] bg-brand px-4 py-2.5 text-center font-semibold text-white" href={`${base}/pdf`} target="_blank" rel="noopener">
            Open the note
          </a>
          <a className="rounded-[var(--radius-control)] border border-line bg-white px-4 py-2.5 text-center font-semibold" href={`${base}/pdf?download=1`}>
            Download PDF
          </a>
        </div>
      </Frame>
    );
  return (
    <Frame clinic={clinic} title="Note">
      <form
        className="mx-auto flex max-w-sm flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await post(`${base}/unlock`, { dob });
            setOpen(true);
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <h1 className="font-display text-2xl">Your {what}</h1>
        <p className="text-ink-soft">To keep it private, enter the date of birth of the person the note is for.</p>
        <DateOfBirthField required value={dob} onChange={setDob} />
        {error && <Alert>{error}</Alert>}
        <Button variant="primary" type="submit" busy={busy}>
          Continue
        </Button>
      </form>
    </Frame>
  );
}
